'use client'
import React from 'react'
import { useAllBreakdown } from '@/src/queries/earnings/breakdown/all'
import { Box, Flex, Image, Text, useBreakpointValue } from '@chakra-ui/react'
import { format, isSameDay, isToday, isYesterday, parseISO } from 'date-fns'

interface TableContentProps {
  type: string
}

const TableContent: React.FC<TableContentProps> = ({ type }) => {
  const { data, status } = useAllBreakdown({
    limit: 6,
    offset: 0,
    filterData: [type],
    sortBy: '',
  })
  const titleCharacterLimit = useBreakpointValue({
    base: 18,
    md: 42,
  }) ?? 42

  let breakdownData: React.ReactNode = null

  const formatWatchTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60

    return `${hours < 10 ? '0' : ''}${hours}:${
      minutes < 10 ? '0' : ''
    }${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
  }

  const getOrdinalSuffix = (day: number) => {
    if (day >= 11 && day <= 13) return 'th'
    switch (day % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  }

  const formatDateLabel = (createdAt: string) => {
    const date = parseISO(createdAt)
    const day = format(date, 'd')
    const dayWithSuffix = `${day}${getOrdinalSuffix(Number(day))}`

    if (isToday(date)) return 'Today'
    if (isYesterday(date)) return 'Yesterday'

    return `${dayWithSuffix} ${format(date, 'MMMM yyyy')}`
  }

  if (status === 'error') {
    breakdownData = null
  }

  if (status === 'success') {
    const videoItems =
      type === 'viewer' || type === 'creator'
        ? data.pages.flatMap((page) =>
            page.allBreakdown.filter((item) => item.type === type)
          )
        : []

    const allItemsHaveZeroWatchTime =
      videoItems.length > 0 && videoItems.every((item) => item.watch_time === "0")

    if (data.pages[0].allBreakdown.length === 0) {
      breakdownData = <Box>No data available</Box>
    } else if (allItemsHaveZeroWatchTime) {
      breakdownData = <Box>Your videos haven’t earned watch-time yet. Appears once people watch while logged in.</Box>
    } else {
      let lastDate: Date | null = null
      breakdownData = data.pages.map((page, i) => (
        <>
          <React.Fragment key={i}>
            {page.allBreakdown
              .filter((item) => item.type === type || item.type === 'bonus')
              .map((item, j) => {
                const currentDate = parseISO(item.createdAt)
                const showDateLabel =
                  !lastDate || !isSameDay(lastDate, currentDate)
                const showWatchCount = Number(item.watchCount) >= 50
                if (item.type === 'viewer' || item.type === 'creator') {
                  if (item.watch_time <= 0) {
                    return null
                  }

                  lastDate = currentDate
                  return (
                    <React.Fragment key={j}>
                      <>
                        {showDateLabel && (
                          <div className="mb-3 flex items-center">
                            <div className="flex-grow border-t-2 border-[#ECECEC]"></div>
                            <span className="mx-1.5 rounded-md bg-[#D9D9D9] px-2 py-0.5 text-xs font-semibold text-[#797979] sm:px-3 sm:text-sm">
                              {formatDateLabel(item.createdAt)}
                            </span>
                            <div className="flex-grow border-t-2 border-[#ECECEC]"></div>
                          </div>
                        )}

                        <Flex
                          key={j}
                          className="mb-3 flex-col justify-between sm:flex-row"
                        >
                          <Flex>
                            <Box
                              mr={{ base: 2.5, sm: 3 }}
                              h={{ base: '52px', sm: '56px' }}
                              w={{ base: '88px', sm: '96px' }}
                            >
                              <Image
                                src={item.thumbnailImage}
                                alt={item.eventTitle}
                                objectFit="cover"
                                borderRadius="6px"
                                aspectRatio={16 / 9}
                              />
                            </Box>
                            <Flex className="flex-col gap-0 text-black sm:gap-1">
                              <Text
                                className="font-medium text-[#656565]"
                                fontSize={{ base: 14, sm: 16, md: 18 }}
                                style={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'pre-wrap',
                                  maxHeight: '3em',
                                  lineHeight: '1.5em',
                                }}
                              >
                                {item.eventTitle.length > titleCharacterLimit
                                  ? item.eventTitle.substring(0, titleCharacterLimit) + '...'
                                  : item.eventTitle}
                              </Text>
                              <Text
                                display={{ base: 'block', md: 'none' }}
                                className="items-center justify-start font-medium text-[#797979]"
                                fontSize={{ base: 13, sm: 14 }}
                              >
                                {item.channelName}
                              </Text>
                              <Flex
                                display={{ base: 'none', md: 'flex' }}
                                className="items-center justify-start font-normal text-[#797979]"
                                fontSize={{ base: 12, sm: 14 }}
                              >
                                {showWatchCount && (
                                  <>
                                    <Text>{item.watchCount} Views</Text>{' '}
                                    <div className="mx-1 h-1 w-1 rounded-full bg-[#797979]"></div>
                                  </>
                                )}
                                <Text>
                                  {formatWatchTime(item.watch_time)} Watched
                                </Text>
                              </Flex>
                            </Flex>
                          </Flex>

                          {/* <Flex
                              className="flex-col text-[#9F9F9F]"
                              display={{ base: 'none', md: 'flex' }}
                            >
                              <Text className="text-center text-[20px] font-semibold">
                                {formatWatchTime(item.watch_time)}
                              </Text>
                              <Text className="text-[16px] font-medium">
                                Watchtime earned
                              </Text>
                            </Flex> */}

                          <Flex
                            display={{ base: 'flex', md: 'none' }}
                            className="mb-3 mt-2 hidden items-center justify-between font-normal text-[#797979] md:flex"
                            fontSize={{ base: 12, sm: 14 }}
                          >
                            {showWatchCount && (
                              <div className="flex flex-col items-center justify-center sm:hidden">
                                <Text className="flex flex-col">
                                  {item.watchCount}
                                </Text>{' '}
                                <Text className="font-medium">Views</Text>
                              </div>
                            )}
                            <div className="flex flex-col items-center justify-center sm:hidden">
                              <Text>{formatWatchTime(item.watch_time)}</Text>{' '}
                              <Text className="font-medium">Watched</Text>
                            </div>

                            <Flex className="flex-col" color="lyk-green">
                              <Text
                                className="text-center font-semibold"
                                fontSize={{ base: 16, sm: 20 }}
                              >
                                {item.lykCoin}
                              </Text>
                              <Flex
                                className="items-center gap-1 font-medium"
                                fontSize={{ base: 12, sm: 14 }}
                              >
                                <Image
                                  boxSize={{ base: 4, sm: 5 }}
                                  src="/logo-color-circle.svg"
                                  alt="Lykstage"
                                />
                                LYK Coins earned
                              </Flex>
                            </Flex>
                          </Flex>

                          <Flex
                            display={{ base: 'none', md: 'flex' }}
                            className="flex-col"
                            color="lyk-green"
                          >
                            <Text
                              className="text-center font-semibold"
                              fontSize={{ base: 16, sm: 18, md: 20 }}
                            >
                              {item.lykCoin}
                            </Text>
                            <Flex
                              className="items-center gap-1 font-medium"
                              fontSize={{ base: 12, sm: 14, md: 16 }}
                            >
                              <Image
                                boxSize={{ base: 4, sm: 5 }}
                                src="/logo-color-circle.svg"
                                alt="Lykstage"
                              />
                              LYK Coins earned
                            </Flex>
                          </Flex>
                        </Flex>
                      </>
                    </React.Fragment>
                  )
                } else if (
                  item.type === 'bonus' ||
                  item.type === 'oldEarnings' ||
                  item.type === 'others_earning'
                ) {
                  lastDate = currentDate
                  return (
                    <React.Fragment key={j}>
                      <>
                        {showDateLabel && (
                          <div className="mb-3 flex items-center">
                            <div className="flex-grow border-t-2 border-[#ECECEC]"></div>
                            <span className="mx-1.5 rounded-md bg-[#D9D9D9] px-2 py-0.5 text-xs font-semibold text-[#797979] sm:px-3 sm:text-sm">
                              {formatDateLabel(item.createdAt)}
                            </span>
                            <div className="flex-grow border-t-2 border-[#ECECEC]"></div>
                          </div>
                        )}

                        <Flex className="mb-3 mt-4 justify-between">
                          <Flex>
                            <Box
                              mr={{ base: 2.5, sm: 3 }}
                              h={{ base: '52px', sm: '56px' }}
                              w={{ base: '88px', sm: '96px' }}
                            >
                              {item.type === 'bonus' ? (
                                <Image
                                  src={'/rewards/gift.svg'}
                                  alt="gift"
                                  objectFit="cover"
                                  aspectRatio={16 / 9}
                                />
                              ) : item.type === 'oldEarnings' ? (
                                <Image
                                  src={'/logo-color-circle.svg'}
                                  alt="gift"
                                  objectFit="cover"
                                  aspectRatio={16 / 9}
                                />
                              ) : (
                                <Image
                                  src={'/rewards/gift.svg'}
                                  alt="gift"
                                  objectFit="cover"
                                  aspectRatio={16 / 9}
                                />
                              )}
                            </Box>

                            <Flex className="flex-col gap-1 text-black">
                              <Text
                                className="font-medium text-[#656565]"
                                fontSize={{ base: 14, sm: 16, md: 18 }}
                                style={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'pre-wrap',
                                  maxHeight: '3em',
                                  lineHeight: '1.5em',
                                }}
                              >
                                {item.type === 'bonus'
                                  ? item.title
                                  : item.type === 'oldEarnings'
                                    // ? 'Old Earnings'
                                    ? 'Old Watch Time Earnings (in LYK Coins)'
                                  : 'Others'}
                              </Text>
                              <Flex
                                className="font-medium text-[#797979]"
                                fontSize={{ base: 12, sm: 14 }}
                              >
                                {item.type === 'bonus' ? (
                                  <Flex
                                    as="ul"
                                    className="m-0 flex list-none flex-row flex-wrap items-center gap-2 p-0"
                                  >
                                    <Text>One time</Text>
                                    <Flex as="li" className="items-center gap-1">
                                      <Box className="h-0.5 w-0.5 rounded-full bg-[#797979]" />
                                      <Text>Incentive</Text>
                                    </Flex>
                                    <Flex as="li" className="items-center gap-1">
                                      <Box className="h-0.5 w-0.5 rounded-full bg-[#797979]" />
                                      <Text>Yours to keep</Text>
                                    </Flex>
                                  </Flex>
                                ) : (
                                  <Text>
                                    {item.type === 'oldEarnings'
                                      ? 'Initiated on 08/19/2024'
                                      : 'One time'}
                                  </Text>
                                )}
                              </Flex>
                              <Text
                                className="font-medium text-[#24BFA3]"
                                fontSize={{ base: 13, sm: 15, md: 16 }}
                                style={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'pre-wrap',
                                  maxHeight: '3em',
                                  lineHeight: '1.5em',
                                }}
                              >
                                {item.type === 'bonus' && item.description}
                              </Text>
                            </Flex>
                          </Flex>

                          <Flex
                            className="flex-col justify-center text-[#9F9F9F]"
                            display={{ base: 'none', sm: 'flex' }}
                          >
                            <Text
                              className="text-center font-bold"
                              fontSize={{ base: 16, sm: 18, md: 20 }}
                            >
                              -
                            </Text>
                          </Flex>

                          <Flex className="flex-col" color="lyk-green">
                            <Text
                              className="text-center font-semibold"
                              fontSize={{ base: 16, sm: 18, md: 20 }}
                            >
                              {item.lykCoin}
                            </Text>
                            <Flex
                              className="items-center gap-1 font-medium"
                              fontSize={{ base: 12, sm: 14, md: 16 }}
                            >
                              <Image
                                boxSize={{ base: 4, sm: 5 }}
                                src="/logo-color-circle.svg"
                                alt="Lykstage"
                              />
                              LYK Coins earned
                            </Flex>
                          </Flex>
                        </Flex>
                      </>
                    </React.Fragment>
                  )
                }
              })}
          </React.Fragment>
        </>
      ))
    }
  }

  return <>{breakdownData}</>
}

export default TableContent
